import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Switch,
  FormControlLabel,
  FormGroup,
  Divider,
  Chip,
  Alert,
  Button
} from '@mui/material';
import {
  Notifications,
  Email,
  PhoneAndroid,
  Sms,
  Schedule,
  VolumeUp,
  VolumeOff,
  NotificationsActive,
  OpenInNew
} from '@mui/icons-material';
import SettingsDialogBase from './SettingsDialogBase';
import apiClient from '../../../../../../api/client';

const NotificationSettingsDialog = ({
  open,
  onClose
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // State for notification preferences (simplified version)
  const [preferences, setPreferences] = useState({
    realtime_enabled: true,
    email_enabled: true,
    push_enabled: true,
    sms_enabled: false,
    user_management_enabled: true,
    content_management_enabled: true,
    vendor_management_enabled: true,
    system_notifications_enabled: true,
    support_notifications_enabled: true,
    high_priority_only: false,
    weekend_notifications: true
  });
  
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  
  // Load current preferences
  const loadPreferences = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/v1/notifications/preferences');
      if (response.data) {
        setPreferences(prev => ({
          ...prev,
          ...response.data
        }));
      }
      
      // Load sound preference from localStorage
      const soundPref = localStorage.getItem('notificationSound');
      setSoundEnabled(soundPref !== 'disabled');    } catch (err) {
      setError(t('notifications.settings.loadError'));
      console.error('Error loading notification preferences:', err);
    }
  }, [t]);
  
  useEffect(() => {
    if (open) {
      loadPreferences();
      setHasChanges(false);
      setSuccess(false);
      setError(null);
    }
  }, [open, loadPreferences]);
  
  // Handle preference changes
  const handlePreferenceChange = (key, value) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
    setHasChanges(true);
  };
  
  // Handle sound toggle
  const handleSoundToggle = () => {
    const newState = !soundEnabled;
    setSoundEnabled(newState);
    localStorage.setItem('notificationSound', newState ? 'enabled' : 'disabled');
    setHasChanges(true);
  };
  
  // Save preferences
  const savePreferences = async () => {
    setSaving(true);
    try {
      await apiClient.put('/api/v1/notifications/preferences', preferences);
      setSuccess(true);
      setHasChanges(false);
      setTimeout(() => {
        onClose();
      }, 1500);    } catch (err) {
      setError(t('notifications.settings.saveError'));
      console.error('Error saving notification preferences:', err);
    }finally {
      setSaving(false);
    }
  };
  
  // Request notification permission
  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setSuccess(true);
      }
    }
  };
  
  // Navigate to full notification settings
  const openFullSettings = () => {
    navigate('/dashboard/notifications/settings');
    onClose();
  };
    return (
    <SettingsDialogBase
      open={open}
      onClose={onClose}
      title={t('notifications.settings.title')}
      description={t('notifications.settings.description')}
      onSave={savePreferences}
      saveDisabled={!hasChanges || saving}
    >
      <Box sx={{ mt: 2 }}>        {/* Status Messages */}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {t('notifications.settings.saveSuccess')}
          </Alert>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
          {/* Quick Link to Full Settings */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.lighter', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="subtitle2" color="primary.main">
                {t('notifications.settings.advancedTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('notifications.settings.advancedDescription')}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<OpenInNew />}
              onClick={openFullSettings}
            >
              {t('notifications.settings.openFullSettings')}
            </Button>
          </Box>
        </Box>
        
        <Grid container spacing={3}>
          {/* Delivery Channels */}
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Notifications color="primary" />
                <Typography variant="h6">{t('notifications.settings.deliveryChannels')}</Typography>
              </Box>
              
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.realtime_enabled}
                      onChange={(e) => handlePreferenceChange('realtime_enabled', e.target.checked)}
                    />
                  }                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <NotificationsActive fontSize="small" />
                      {t('notifications.settings.channels.realtime')}
                    </Box>
                  }
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.email_enabled}
                      onChange={(e) => handlePreferenceChange('email_enabled', e.target.checked)}
                    />
                  }                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Email fontSize="small" />
                      {t('notifications.settings.channels.email')}
                    </Box>
                  }
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.push_enabled}
                      onChange={(e) => handlePreferenceChange('push_enabled', e.target.checked)}
                    />
                  }                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneAndroid fontSize="small" />
                      {t('notifications.settings.channels.push')}
                      <Chip label={t('common.future')} size="small" variant="outlined" color="info" />
                    </Box>
                  }
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.sms_enabled}
                      onChange={(e) => handlePreferenceChange('sms_enabled', e.target.checked)}
                    />
                  }                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Sms fontSize="small" />
                      {t('notifications.settings.channels.sms')}
                      <Chip label={t('common.future')} size="small" variant="outlined" color="info" />
                    </Box>
                  }
                />
              </FormGroup>
              
              <Divider sx={{ my: 2 }} />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={soundEnabled}
                    onChange={handleSoundToggle}
                  />
                }                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {soundEnabled ? <VolumeUp fontSize="small" /> : <VolumeOff fontSize="small" />}
                    {t('notifications.settings.sound')}
                  </Box>
                }
              />
              
              {Notification.permission === 'default' && (
                <Box sx={{ mt: 2 }}>                  <Button
                    variant="outlined"
                    size="small"
                    onClick={requestNotificationPermission}
                    startIcon={<NotificationsActive />}
                  >
                    {t('notifications.settings.enableBrowser')}
                  </Button>
                </Box>
              )}
            </Box>
          </Grid>
          
          {/* Category Preferences */}
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('notifications.settings.categories')}
              </Typography>
              
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.user_management_enabled}
                      onChange={(e) => handlePreferenceChange('user_management_enabled', e.target.checked)}
                    />
                  }
                  label={t('notifications.settings.category.userManagement')}
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.content_management_enabled}
                      onChange={(e) => handlePreferenceChange('content_management_enabled', e.target.checked)}
                    />
                  }
                  label={t('notifications.settings.category.contentManagement')}
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.vendor_management_enabled}
                      onChange={(e) => handlePreferenceChange('vendor_management_enabled', e.target.checked)}
                    />
                  }
                  label={t('notifications.settings.category.vendorManagement')}
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.system_notifications_enabled}
                      onChange={(e) => handlePreferenceChange('system_notifications_enabled', e.target.checked)}
                    />
                  }
                  label={t('notifications.settings.category.system')}
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.support_notifications_enabled}
                      onChange={(e) => handlePreferenceChange('support_notifications_enabled', e.target.checked)}
                    />
                  }
                  label={t('notifications.settings.category.support')}
                />
              </FormGroup>
            </Box>
          </Grid>
          
          {/* Priority & Schedule Settings */}
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Schedule color="primary" />
                <Typography variant="h6">{t('notifications.settings.prioritySchedule')}</Typography>
              </Box>
              
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.high_priority_only}
                      onChange={(e) => handlePreferenceChange('high_priority_only', e.target.checked)}
                    />
                  }
                  label={t('notifications.settings.highPriorityOnly')}
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.weekend_notifications}
                      onChange={(e) => handlePreferenceChange('weekend_notifications', e.target.checked)}
                    />
                  }
                  label={t('notifications.settings.weekendNotifications')}
                />
              </FormGroup>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('notifications.settings.advancedSchedulingNote')}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </SettingsDialogBase>
  );
};

export default NotificationSettingsDialog;